import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Empty,
    InputNumber,
    Row,
    Select,
    Skeleton,
    Space,
    Switch,
    Tabs,
    Typography,
} from 'antd';
import {
    DatabaseOutlined,
    FileSearchOutlined,
    FolderOpenOutlined,
    InfoCircleOutlined,
    NodeIndexOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { aiAdminApi, liveSettingsKeys } from '../../../services/ai-admin-api';
import { useSubsystemEditorContext } from '../hooks/useSubsystemDraftEditor';
import { SubsystemDraftActions } from '../components/SubsystemDraftActions';

const { Text, Paragraph } = Typography;

interface KnowledgeBasePayload {
    schemaVersion: 1;
    systemSettingsKeys: Record<string, string | undefined>;
    configTableRows: Record<string, never>;
}

function asBool(v: string | undefined): boolean {
    return (v ?? 'false').toString().toLowerCase() === 'true';
}

function asInt(v: string | undefined, fallback = 0): number {
    if (v === undefined || v === null || v === '') return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? Math.round(n) : fallback;
}

function asNumber(v: string | undefined, fallback = 0): number {
    if (v === undefined || v === null || v === '') return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
}

function parseModelList(raw: string | undefined): Array<{ id: string; label?: string }> {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((entry: unknown) => {
                if (typeof entry === 'string') return { id: entry };
                if (entry && typeof entry === 'object') {
                    const e = entry as { id?: string; label?: string };
                    if (e.id) return { id: e.id, label: e.label };
                }
                return null;
            })
            .filter((m): m is { id: string; label?: string } => m !== null);
    } catch {
        return [];
    }
}

/**
 * Configure tab body for Knowledge Base (RAG).
 *
 * Knowledge documents and chunks are content, not config — they are NOT
 * versioned by this subsystem (per PLAN.md §3.5) and are managed from the
 * existing AI Experts Documents admin page. This Configure tab only owns
 * the six retrieval/logging knobs.
 *
 * Today only ENABLE_KNOWLEDGE_BASE_LOGS is read at runtime; the chunking +
 * retrieval keys are forward-looking and surface for the eventual RAG
 * implementation.
 */
export const KnowledgeBaseConfigurePage: React.FC = () => {
    const editor = useSubsystemEditorContext<KnowledgeBasePayload>();
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const navigate = useNavigate();
    const [embeddingModels, setEmbeddingModels] = useState<Array<{ id: string; label?: string }>>([]);

    useEffect(() => {
        if (!jwt) return;
        aiAdminApi
            .getSubsystem(jwt, 'models_providers')
            .then((mp) => {
                const keys = liveSettingsKeys(mp);
                setEmbeddingModels(
                    parseModelList(keys.AVAILABLE_EMBEDDING_MODELS),
                );
            })
            .catch(() => {
                // Non-fatal — admin can still type a model id manually.
            });
    }, [jwt]);

    if (editor.isLoading || !editor.payload) {
        return <Skeleton active paragraph={{ rows: 12 }} />;
    }

    const settings = editor.payload.systemSettingsKeys ?? {};
    if (!settings || Object.keys(settings).length === 0) {
        return <Empty description="No knowledge-base settings — has the v1 baseline been seeded?" />;
    }

    const setSetting = (key: string, value: string) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, [key]: value },
        }));
    };

    return (
        <div>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
                <Text type="secondary">
                    Document store, chunking, embeddings, retrieval knobs. Knowledge documents
                    themselves are managed from the AI Experts Documents page — only the
                    retrieval-pipeline configuration lives here.
                </Text>
                <Space wrap>
                    <Button
                        icon={<FolderOpenOutlined />}
                        onClick={() => navigate('/admin/ai/knowledge_base/documents')}
                    >
                        Manage documents
                    </Button>
                    <SubsystemDraftActions
                        detail={editor.detail}
                        isDirty={editor.isDirty}
                        isSaving={editor.isSaving}
                        isPublishing={editor.isPublishing}
                        isDiscarding={editor.isDiscarding}
                        onSaveDraft={() => editor.saveDraft()}
                        onDiscardDraft={() => editor.discardDraft()}
                    />
                </Space>
            </Space>

            <Card>
                <Tabs
                    tabPosition="left"
                    style={{ minHeight: 500 }}
                    items={[
                        {
                            key: 'logging',
                            label: (
                                <span>
                                    <DatabaseOutlined style={{ marginRight: 8, color: '#3b82f6' }} />
                                    Logging
                                </span>
                            ),
                            children: <LoggingTab settings={settings} setSetting={setSetting} />,
                        },
                        {
                            key: 'chunking',
                            label: (
                                <span>
                                    <NodeIndexOutlined style={{ marginRight: 8, color: '#10b981' }} />
                                    Chunking
                                </span>
                            ),
                            children: <ChunkingTab settings={settings} setSetting={setSetting} />,
                        },
                        {
                            key: 'retrieval',
                            label: (
                                <span>
                                    <FileSearchOutlined style={{ marginRight: 8, color: '#f97316' }} />
                                    Retrieval
                                </span>
                            ),
                            children: (
                                <RetrievalTab
                                    settings={settings}
                                    setSetting={setSetting}
                                    embeddingModels={embeddingModels}
                                />
                            ),
                        },
                    ]}
                />
            </Card>
        </div>
    );
};

interface SettingsTabProps {
    settings: Record<string, string | undefined>;
    setSetting: (key: string, value: string) => void;
}

const LoggingTab: React.FC<SettingsTabProps> = ({ settings, setSetting }) => {
    const enabled = asBool(settings.ENABLE_KNOWLEDGE_BASE_LOGS);
    return (
        <div>
            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
                message="Knowledge base logging"
                description="Verbose debug logs for knowledge-base ops (vector lookups, retrieval scoring, chunk hits). Useful when tuning prompts or diagnosing why an expert isn't grounding correctly. Noisy in production — leave off by default."
            />

            <Space direction="vertical" size={2}>
                <Text strong>Knowledge base logs enabled</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    <code>ENABLE_KNOWLEDGE_BASE_LOGS</code> — read by base-expert, shared-knowledge,
                    and vector-database services.
                </Text>
                <Switch
                    checked={enabled}
                    onChange={(checked) =>
                        setSetting('ENABLE_KNOWLEDGE_BASE_LOGS', checked ? 'true' : 'false')
                    }
                />
            </Space>
        </div>
    );
};

const FORWARD_LOOKING_NOTE = (
    <span>
        Not yet read at runtime — these knobs surface for the in-flight RAG
        implementation. Configuring them now means values are in place the moment the new
        retrieval pipeline lands.
    </span>
);

const ChunkingTab: React.FC<SettingsTabProps> = ({ settings, setSetting }) => {
    const chunkSize = asInt(settings.RAG_CHUNK_SIZE, 1000);
    const chunkOverlap = asInt(settings.RAG_CHUNK_OVERLAP, 100);
    return (
        <div>
            <Alert
                type="warning"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
                message="Forward-looking — chunk-time settings"
                description={FORWARD_LOOKING_NOTE}
            />

            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Text strong>Chunk size</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <code>RAG_CHUNK_SIZE</code> — characters per chunk during ingestion.
                    </Text>
                    <br />
                    <InputNumber
                        min={100}
                        step={100}
                        style={{ width: 200, marginTop: 4 }}
                        value={chunkSize}
                        onChange={(v) => setSetting('RAG_CHUNK_SIZE', String(v ?? 0))}
                        addonAfter="chars"
                    />
                </Col>
                <Col xs={24} md={12}>
                    <Text strong>Chunk overlap</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <code>RAG_CHUNK_OVERLAP</code> — characters of overlap between adjacent
                        chunks (helps preserve context across boundaries).
                    </Text>
                    <br />
                    <InputNumber
                        min={0}
                        step={50}
                        style={{ width: 200, marginTop: 4 }}
                        value={chunkOverlap}
                        onChange={(v) => setSetting('RAG_CHUNK_OVERLAP', String(v ?? 0))}
                        addonAfter="chars"
                    />
                </Col>
            </Row>

            <Paragraph type="secondary" style={{ marginTop: 16 }}>
                Re-chunking existing documents requires a re-embed — kicked off from the AI
                Experts Documents page once the new pipeline is in place.
            </Paragraph>
        </div>
    );
};

interface RetrievalTabProps extends SettingsTabProps {
    embeddingModels: Array<{ id: string; label?: string }>;
}

const RetrievalTab: React.FC<RetrievalTabProps> = ({
    settings,
    setSetting,
    embeddingModels,
}) => {
    const topK = asInt(settings.RAG_DEFAULT_TOP_K, 5);
    const minRelevancePct = Math.round(asNumber(settings.RAG_DEFAULT_MIN_RELEVANCE, 0.7) * 100);
    const embeddingModel = settings.RAG_EMBEDDING_MODEL ?? '';
    const knownIds = new Set(embeddingModels.map((m) => m.id));
    const isOrphan = !!embeddingModel && !knownIds.has(embeddingModel);

    const modelOptions: Array<{ value: string; label: string }> = [];
    if (isOrphan) {
        modelOptions.push({
            value: embeddingModel,
            label: `${embeddingModel} (not in embedding-models list — mismatch?)`,
        });
    }
    for (const m of embeddingModels) {
        modelOptions.push({ value: m.id, label: m.label ?? m.id });
    }

    return (
        <div>
            <Alert
                type="warning"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
                message="Forward-looking — retrieval defaults"
                description={FORWARD_LOOKING_NOTE}
            />

            <Row gutter={16}>
                <Col xs={24} md={8}>
                    <Text strong>Default top K</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <code>RAG_DEFAULT_TOP_K</code> — how many chunks to fetch per query.
                    </Text>
                    <br />
                    <InputNumber
                        min={1}
                        step={1}
                        style={{ width: 140, marginTop: 4 }}
                        value={topK}
                        onChange={(v) => setSetting('RAG_DEFAULT_TOP_K', String(v ?? 1))}
                    />
                </Col>
                <Col xs={24} md={8}>
                    <Text strong>Default min relevance</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <code>RAG_DEFAULT_MIN_RELEVANCE</code> — minimum cosine similarity
                        threshold (stored as decimal, 0–1).
                    </Text>
                    <br />
                    <InputNumber
                        min={0}
                        max={100}
                        step={5}
                        style={{ width: 140, marginTop: 4 }}
                        value={minRelevancePct}
                        onChange={(v) =>
                            setSetting(
                                'RAG_DEFAULT_MIN_RELEVANCE',
                                String(Math.max(0, Math.min(100, Number(v ?? 0))) / 100),
                            )
                        }
                        formatter={(v) => `${v ?? 0}%`}
                        parser={(v) => Number((v ?? '0').toString().replace('%', ''))}
                    />
                </Col>
                <Col xs={24} md={8}>
                    <Text strong>Embedding model</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <code>RAG_EMBEDDING_MODEL</code> — pulled from the Models &amp; Providers
                        embedding tab.
                    </Text>
                    <br />
                    <Select
                        style={{ width: '100%', marginTop: 4 }}
                        value={embeddingModel}
                        onChange={(v) => setSetting('RAG_EMBEDDING_MODEL', v)}
                        options={modelOptions}
                        placeholder="Select embedding model"
                        showSearch
                        optionFilterProp="label"
                    />
                </Col>
            </Row>

            <Paragraph type="secondary" style={{ marginTop: 16 }}>
                Switching embedding models invalidates the existing vector index — every
                document needs to be re-embedded against the new model, kicked off from the AI
                Experts Documents page.
            </Paragraph>
        </div>
    );
};
